import React, { createContext, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ar from "./ar.json";
import en from "./en.json";

const DICTS = { ar, en };
export const SUPPORTED = ["ar", "en"];
export const DEFAULT_LANG = "ar";

const I18nContext = createContext(null);

function get(obj, path) {
  return path.split(".").reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

export function detectLangFromPath(pathname) {
  const seg = pathname.split("/").filter(Boolean)[0];
  return SUPPORTED.includes(seg) ? seg : DEFAULT_LANG;
}

export function I18nProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = detectLangFromPath(location.pathname);

  const value = useMemo(() => {
    const dict = DICTS[lang];
    const t = (key, fallback = "") => {
      const val = get(dict, key);
      return val === undefined || val === null ? fallback || key : val;
    };
    const switchLang = (newLang) => {
      if (!SUPPORTED.includes(newLang) || newLang === lang) return;
      const parts = location.pathname.split("/").filter(Boolean);
      if (SUPPORTED.includes(parts[0])) {
        parts[0] = newLang;
      } else {
        parts.unshift(newLang);
      }
      const target = "/" + parts.join("/");
      // sessionStorage flag so ScrollToTop knows this is a language switch
      sessionStorage.setItem("xq_lang_switch", "1");
      navigate(target + location.search + location.hash);
    };
    const localePath = (subPath = "") => {
      const clean = subPath.replace(/^\/+/, "");
      return "/" + lang + (clean ? "/" + clean : "");
    };
    return { lang, t, switchLang, localePath };
  }, [lang, location, navigate]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
